import React, { useEffect, useMemo, useRef, useState } from 'react';
import './Chat.css';
import { useAuth } from '../../AuthContext';
import { apiUrl } from '../../api/client';
import SiteHeader from '../../shared/SiteHeader';
import AdSense from '../../shared/AdSense';
import type { ChatMessageDto } from '../../types/ChatMessageDto';
import type { Conversation } from '../../types/Conversation';
import type { ConversationInfo } from '../../types/ConversationInfo';
import {
  setCurrentConversationId,
  clearCurrentConversationId,
  setConversationList,
} from '../../store/conversationsSlice';
import Spinner from '../../shared/Spinner';
import { createApiClient } from '../../api/ApiClient';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import MessageFeedback from '../../shared/MessageFeedback';
import { ApiRoutes } from '../../types/ApiRoutes';
import { isAdsEnabled } from '../../utils/featureFlags';

const Chat: React.FC = () => {
  const [question, setQuestion] = useState('');
  // Get conversation ID from Redux store
  const conversationId = useAppSelector((state) => state.conversations.currentConversationId);
  const conversationList = useAppSelector((state) => state.conversations.conversationList);
  const conversationListLoaded = useAppSelector(
    (state) => state.conversations.conversationListLoaded
  );
  const dispatch = useAppDispatch();
  // Each chat message now stores a messageId for future referencing via API calls.
  const [messages, setMessages] = useState<ChatMessageDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [adRefreshKey, setAdRefreshKey] = useState(0);
  const threadRef = useRef<HTMLDivElement | null>(null);
  const lastUserMsgRef = useRef<HTMLDivElement | null>(null);
  const chatQuestionRef = useRef<HTMLTextAreaElement | null>(null);

  const TEXTAREA_MAX_ROWS = 3;

  useEffect(() => {
    const el = chatQuestionRef.current;
    if (!el) {
      return;
    }
    el.style.height = 'auto';
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20;
    const paddingY =
      parseFloat(getComputedStyle(el).paddingTop) + parseFloat(getComputedStyle(el).paddingBottom);
    const maxHeight = lineHeight * TEXTAREA_MAX_ROWS + paddingY;
    if (el.scrollHeight <= maxHeight) {
      el.style.height = `${el.scrollHeight}px`;
      el.style.overflowY = 'hidden';
    } else {
      el.style.height = `${maxHeight}px`;
      el.style.overflowY = 'auto';
    }
  }, [question]);
  const [showConversationMenu, setShowConversationMenu] = useState(false);
  const conversationButtonRef = useRef<HTMLButtonElement | null>(null);
  const conversationMenuRef = useRef<HTMLDivElement | null>(null);

  const { authorizedFetch } = useAuth();
  const api = useMemo(() => createApiClient(authorizedFetch), [authorizedFetch]);
  const lastUserIndex = messages.reduce((acc, m, idx) => (m.role === 'user' ? idx : acc), -1);

  const handleNewChat = () => {
    dispatch(clearCurrentConversationId());
    setMessages([]);
    setError('');
    setQuestion('');
    setShowConversationMenu(false);
    // Retrieve a new chat message for the new conversation
    fetchNewChat().then(() => {
      fetchConversationList();
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setAdRefreshKey((k) => k + 1); // increment here so new ad loads during generation delay
    const trimmed = question.trim();
    if (!trimmed) {
      setLoading(false);
      return;
    }
    // Optimistically add user message with a client-generated id (server assigns its own for assistant response).
    const clientUserMessageId = `client-${crypto.randomUUID?.() || Math.random().toString(36).slice(2)}`;
    setMessages((prev) => [
      ...prev,
      {
        role: 'user',
        content: trimmed,
        messageId: clientUserMessageId,
        conversationId: conversationId || '',
      },
    ]);

    setQuestion('');
    try {
      const data = await api.post<Partial<ChatMessageDto> & Record<string, unknown>>(
        apiUrl(ApiRoutes.Chat),
        { conversationId: conversationId ?? null, message: trimmed },
        { errorMessage: 'Failed to fetch chat response' }
      );

      if (data?.conversationId) {
        dispatch(setCurrentConversationId(data.conversationId));
      }

      // const assistantHtml = (data?.content ?? data?.message ?? '') as string;
      // const assistantMessageId = (data?.messageId ??
      //   data?.id ??
      //   `server-${Math.random().toString(36).slice(2)}`) as string;
      setMessages((prev) => [
        ...prev,
        data as ChatMessageDto,
        // {
        //   role: 'assistant',
        //   content: assistantHtml,
        //   messageId: assistantMessageId,
        //   conversationId: convId || '',
        // },
      ]);
      void fetchConversationList();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Retrieve the new chat message from the API
  const fetchNewChat = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await api.get<Partial<ChatMessageDto> & Record<string, unknown>>(
        apiUrl(ApiRoutes.NewChat),
        { errorMessage: 'Failed to fetch new chat response' }
      );

      if (data.conversationId) {
        dispatch(setCurrentConversationId(data.conversationId));
      }
      // Replace thread with the new chat assistant message
      setMessages([data as ChatMessageDto]);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Retrieve the initial conversation from the API
  const fetchLatestConversation = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await api.get<Conversation>(apiUrl(ApiRoutes.LatestConversation), {
        errorMessage: 'Failed to fetch latest conversation',
      });

      if (data?.conversationId) {
        dispatch(setCurrentConversationId(data.conversationId));
      }

      // Load all messages from the conversation, excluding system messages
      const loadedMessages: ChatMessageDto[] =
        data?.messages?.filter((m) => m.role !== 'system') ?? [];

      setMessages(loadedMessages);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to load a specific conversation by ID
  const fetchConversationById = async (id: string) => {
    try {
      setLoading(true);
      setError('');
      const data = await api.get<Conversation>(apiUrl(`${ApiRoutes.Conversations}/${id}`), {
        errorMessage: 'Failed to fetch conversation',
      });

      if (data?.conversationId) {
        dispatch(setCurrentConversationId(data.conversationId));
      }

      const loadedMessages: ChatMessageDto[] =
        data?.messages?.filter((m) => m.role !== 'system') ?? [];

      setMessages(loadedMessages);
    } catch (err: unknown) {
      // If loading fails, fall back to latest conversation
      console.error('Failed to load conversation, fetching latest:', err);
      void fetchLatestConversation();
    } finally {
      setLoading(false);
    }
  };

  // Fetch the list of conversations for the user
  const fetchConversationList = async () => {
    try {
      const data = await api.get<ConversationInfo[]>(apiUrl(ApiRoutes.Conversations), {
        errorMessage: 'Failed to fetch conversation list',
      });

      if (data) {
        dispatch(setConversationList(data));
      }
    } catch (err: unknown) {
      console.error('Failed to load conversation list:', err);
      // Don't set error state, this is a background operation
    }
  };

  const handleDeleteConversation = async (convId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the conversation select

    if (!window.confirm('Are you sure you want to delete this conversation?')) {
      return;
    }

    try {
      await api.delete(apiUrl(`${ApiRoutes.Conversations}/${convId}`), {
        errorMessage: 'Failed to delete conversation',
      });

      // Remove from Redux store
      const updatedList = conversationList.filter((c) => c.conversationId !== convId);
      dispatch(setConversationList(updatedList));

      // If we deleted the current conversation, load the latest one
      if (convId === conversationId) {
        dispatch(clearCurrentConversationId());
        setMessages([]);
        if (updatedList.length > 0) {
          void fetchConversationById(updatedList[0].conversationId);
        } else {
          void handleNewChat();
        }
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete conversation');
    }
  };

  useEffect(() => {
    document.title = 'Walk It With Me - Chat';
  }, []);

  // On mount, fetch the latest conversation or load the stored conversation
  useEffect(() => {
    // Load conversation list if not already loaded
    if (!conversationListLoaded) {
      void fetchConversationList();
    }

    if (messages.length === 0) {
      if (conversationId) {
        // Load the conversation from Redux state
        void fetchConversationById(conversationId);
      } else {
        // No stored conversation, fetch the latest
        void fetchLatestConversation();
      }
    }
  }, []);

  // Scroll behavior on new messages:
  // Always scroll the latest user bubble to the top of the thread (after user submit and after assistant reply)
  useEffect(() => {
    if (!messages.length) {
      return;
    }
    if (lastUserMsgRef.current) {
      lastUserMsgRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [messages]);

  const handleConversationSelect = (convId: string) => {
    setShowConversationMenu(false);
    if (convId === conversationId) {
      return; // Already loaded
    }
    void fetchConversationById(convId);
  };

  // Close user menu on outside click or Escape key
  useEffect(() => {
    const handleDocumentMouseDown = (e: MouseEvent) => {
      if (!showConversationMenu) {
        return;
      }
      const target = e.target as Node;
      if (conversationButtonRef.current && conversationButtonRef.current.contains(target)) {
        return;
      }
      if (conversationMenuRef.current && conversationMenuRef.current.contains(target)) {
        return;
      }
      setShowConversationMenu(false);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!showConversationMenu) {
        return;
      }
      if (e.key === 'Escape') {
        setShowConversationMenu(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showConversationMenu]);

  const adClient = process.env.REACT_APP_ADSENSE_CLIENT_ID || '';
  const adSlotVertical = process.env.REACT_APP_ADSENSE_SLOT_VERTICAL || '';
  const adSlotHorizontal = process.env.REACT_APP_ADSENSE_SLOT_HORIZONTAL || '';

  return (
    <>
      <SiteHeader />
      <div className="container py-5 chat-page">
        <div className="row">
          <div className="col-12 col-lg-9">
            <div className="chat-body p-3 p-md-4">
              {messages.length === 0 && !loading && !error && (
                <div className="alert alert-info" role="alert">
                  No messages yet. Start the conversation by typing a question below.
                </div>
              )}

              <div className="chat-thread mt-2" ref={threadRef} aria-live="polite">
                {messages.map((m, idx) => (
                  <div
                    key={idx}
                    ref={idx === lastUserIndex ? lastUserMsgRef : undefined}
                    className={`chat-msg ${m.role === 'user' ? 'msg-user' : 'msg-assistant'}`}
                    data-message-id={m.messageId}
                  >
                    {m.role === 'user' ? (
                      <>
                        <div className="bubble">{m.content}</div>
                      </>
                    ) : (
                      <>
                        <div className="bubble" dangerouslySetInnerHTML={{ __html: m.content }} />
                        {m.messageId && <MessageFeedback message={m} />}
                      </>
                    )}
                  </div>
                ))}
                {/* Inline ad — small/medium screens only; refreshes on each message send */}
                {adRefreshKey > 0 && isAdsEnabled() && (
                  <div key={adRefreshKey} className="chat-ad-inline d-block d-lg-none">
                    <span className="chat-ad-label">Sponsored</span>
                    <AdSense
                      adClient={adClient}
                      adSlot={adSlotHorizontal}
                      adFormat="horizontal"
                      fullWidthResponsive={true}
                    />
                  </div>
                )}
                <Spinner visible={loading} ariaLabel="Thinking…" />
              </div>

              {error && (
                <div className="alert alert-danger mt-3" role="alert">
                  {error}
                </div>
              )}
              <form onSubmit={handleSubmit} className="chat-form chat-form-fixed mt-3">
                <div className="container container-sm container-md container-lg container-xl">
                  <div className="row justify-content-center">
                    <div className="col-12 col-lg-10 bg-light chat-form-input">
                      <div className="row g-2 align-items-end">
                        <div className="col">
                          <label htmlFor="chatQuestion" className="visually-hidden">
                            Question
                          </label>
                          <textarea
                            id="chatQuestion"
                            ref={chatQuestionRef}
                            className="form-control"
                            placeholder="Type your question..."
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                if (!loading && question.trim()) {
                                  e.currentTarget.form?.requestSubmit();
                                }
                              }
                            }}
                            rows={1}
                            style={{ resize: 'none' }}
                            required
                          />
                        </div>
                        <div className="col-auto d-grid">
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading || !question.trim()}
                            aria-label={loading ? 'Sending…' : 'Send'}
                          >
                            <i className="bi bi-send me-md-2" aria-hidden="true"></i>
                            <span className="d-none d-md-inline">
                              {loading ? 'Sending...' : 'Send'}
                            </span>
                          </button>
                        </div>
                        <div className="col-auto d-grid position-relative">
                          <button
                            type="button"
                            className="btn btn-success dropdown-toggle"
                            ref={conversationButtonRef}
                            onClick={() => setShowConversationMenu(!showConversationMenu)}
                            disabled={loading}
                            aria-label="Select Conversation"
                            aria-expanded={showConversationMenu}
                          >
                            <i className="bi bi-list me-md-2" aria-hidden="true"></i>
                            <span className="visually-hidden">Conversations</span>
                          </button>
                          {showConversationMenu && (
                            <div
                              className="dropdown-menu show position-absolute conversation-dropdown-menu"
                              ref={conversationMenuRef}
                            >
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={handleNewChat}
                              >
                                <i className="bi bi-plus-lg me-2"></i>
                                New Conversation
                              </button>
                              <div className="dropdown-divider"></div>
                              {conversationList.length === 0 ? (
                                <span className="dropdown-item-text text-muted">
                                  No conversations
                                </span>
                              ) : (
                                conversationList.map((conv) => (
                                  <div
                                    key={conv.conversationId}
                                    className="conversation-item-wrapper"
                                  >
                                    <button
                                      type="button"
                                      title={conv.name}
                                      className={`dropdown-item conversation-item ${conv.conversationId === conversationId ? 'active' : ''}`}
                                      onClick={() => handleConversationSelect(conv.conversationId)}
                                    >
                                      <span className="text-truncate conversation-name">
                                        {conv.name}
                                      </span>
                                    </button>
                                    <span
                                      className="delete-conversation-btn text-danger"
                                      onClick={(e) =>
                                        handleDeleteConversation(conv.conversationId, e)
                                      }
                                      role="button"
                                      tabIndex={0}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                          handleDeleteConversation(
                                            conv.conversationId,
                                            e as unknown as React.MouseEvent
                                          );
                                        }
                                      }}
                                      aria-label="Delete conversation"
                                      title="Delete conversation"
                                    >
                                      <i className="bi bi-trash-fill"></i>
                                    </span>
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Vertical ad sidebar — large screens only; refreshes on each message send */}
          <div className="col-lg-3 d-none d-lg-flex flex-column chat-ad-sidebar">
            {isAdsEnabled() && (
              <div key={adRefreshKey}>
                <AdSense adClient={adClient} adSlot={adSlotVertical} adFormat="auto" />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Chat;
